import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AssetsGalleryEmptyState from "domains/assets/components/AssetsGalleryEmptyState";
import useAllAssets from "domains/assets/hooks/useAllAssets";
import useAssetFilterType from "domains/assets/hooks/useAssetFilterType";
import useAssetSort from "domains/assets/hooks/useAssetSort";
import useAssetView from "domains/assets/hooks/useAssetView";
import { PersistedStateKey } from "domains/commons/hooks/usePersistedState";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import Search from "domains/file-manager/components/Search";
import { mapAssetsToImagesFiles } from "domains/file-manager/interfaces";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useAssetsSearch } from "domains/search/hooks/useSearch";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  useGetModelsExamplesByModelIdQuery,
  usePutModelsExamplesByModelIdMutation,
} from "infra/api/generated/api";

import { Box, Flex, Text, VStack } from "@chakra-ui/react";

import ModelShowImagesExamples from "./Examples";

export interface ModelShowImagesProps {
  model: GetModelsByModelIdApiResponse["model"];
  contentScrollRef?: React.RefObject<HTMLDivElement>;
}

export default function ModelShowImages({
  model,
  contentScrollRef,
}: ModelShowImagesProps) {
  const [putModelsExamplesByModelId] = usePutModelsExamplesByModelIdMutation();
  const { successToast } = useScenarioToast();
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [searchQuery, setSearchQuery] = useState("");
  const pinnedAssetIdsRef = useRef<string[]>([]);
  const sectionExamplesRef = useRef<HTMLDivElement>(null);
  const [positionerDeps, setPositionerDeps] = useState<[string] | []>([]);

  const { data: modelExamples, isLoading: isLoadingPinnedAssets } =
    useGetModelsExamplesByModelIdQuery({
      projectId: selectedProject.id,
      modelId: model.id,
    });

  const fmViewProps = useAssetView({
    options: ["masonry", "fill", "fit", "jobs"],
  });
  const { allAssetsTypeArgs, fmHeaderFilterAssetProps } = useAssetFilterType({
    assetType: "all",
  });
  const { allAssetsSortArgs, fmHeaderSortProps } = useAssetSort();

  const { files, jobs, isLoading, hasMore, loadMore } = useAllAssets({
    modelId: model.id,
    ...allAssetsTypeArgs,
    ...allAssetsSortArgs,
  });

  const searchResponse = useAssetsSearch({
    query: searchQuery,
    modelIds: [model.id],
    ...allAssetsTypeArgs,
    ...allAssetsSortArgs,
  });

  const isOwner = model?.ownerId === selectedProject.id;
  const isSearching = !!searchQuery;

  const isPinnedAssetsDisplayed = !!modelExamples?.examples?.length;
  const pinnedAssetsFiles = useMemo(() => {
    return mapAssetsToImagesFiles(
      modelExamples?.examples.map((example) => example.asset) ?? []
    );
  }, [modelExamples]);
  const pinnedAssetIds = useMemo(
    () => pinnedAssetsFiles.map((file) => file.id),
    [pinnedAssetsFiles]
  );

  // ----------------------------------

  useEffect(() => {
    pinnedAssetIdsRef.current = pinnedAssetIds;
  }, [pinnedAssetIds]);

  // ----------------------------------

  const unpinAssetToModel = useCallback(
    async (assetId: string) => {
      try {
        const newPinnedAssetIds = pinnedAssetIdsRef.current.filter(
          (id) => id !== assetId
        );
        pinnedAssetIdsRef.current = newPinnedAssetIds;

        await putModelsExamplesByModelId({
          modelId: model.id,
          projectId: selectedProject.id,
          body: {
            assetIds: newPinnedAssetIds,
          },
        });

        successToast({
          title: "The pinned image has been removed",
        });
      } catch (error) {
        pinnedAssetIdsRef.current = [...pinnedAssetIdsRef.current, assetId];
        handleApiError(error, "There was an error removing the model example");
      }
    },
    [
      handleApiError,
      model.id,
      putModelsExamplesByModelId,
      selectedProject.id,
      successToast,
    ]
  );

  const pinAssetToModel = useCallback(
    async (assetId: string) => {
      try {
        const newPinnedAssetIds = [...pinnedAssetIdsRef.current, assetId];
        pinnedAssetIdsRef.current = newPinnedAssetIds;

        await putModelsExamplesByModelId({
          modelId: model.id,
          projectId: selectedProject.id,
          body: {
            assetIds: newPinnedAssetIds,
          },
        });
        successToast({
          title: "Image pinned",
        });
      } catch (error) {
        pinnedAssetIdsRef.current = pinnedAssetIdsRef.current.filter(
          (id) => id !== assetId
        );
        handleApiError(error, "There was an error pinning the image");
      }
    },
    [
      putModelsExamplesByModelId,
      model.id,
      selectedProject.id,
      successToast,
      handleApiError,
    ]
  );

  // ----------------------------------

  const handleCardPinClick = useCallback(
    async (file: FmFileImage, isPinned = false) => {
      if (isPinned) await pinAssetToModel(file.id);
      else await unpinAssetToModel(file.id);
    },
    [pinAssetToModel, unpinAssetToModel]
  );

  // ----------------------------------

  useEffect(() => {
    if (!sectionExamplesRef.current) return;

    if (isSearching) {
      setPositionerDeps(["searching"]);
      return;
    }

    const observer = new ResizeObserver(() => {
      setPositionerDeps([`example-${new Date().getTime()}`]);
    });
    const current = sectionExamplesRef.current;
    observer.observe(current);
    return () => current && observer.unobserve(current);
  }, [sectionExamplesRef, isSearching]);

  // ----------------------------------

  const emptyStateProps = useMemo(
    () => ({
      modelId: model.id,
      withoutGenerateButton: model.status !== "trained",
    }),
    [model.id, model.status]
  );

  // ----------------------------------

  return (
    <VStack align="stretch" flex="1" bgColor="background.500" id="modelScroll">
      <Box ref={sectionExamplesRef}>
        {!isSearching && (
          <ModelShowImagesExamples
            contentScrollRef={contentScrollRef}
            files={pinnedAssetsFiles}
            ids={pinnedAssetIds}
            isLoading={isLoadingPinnedAssets}
            onCardPinClick={isOwner ? handleCardPinClick : undefined}
          />
        )}
      </Box>

      <Box pb={9}>
        <Flex direction="column" pt="0" data-testid="asset-gallery">
          {isPinnedAssetsDisplayed && !isSearching && (
            <Text size="title.md">All</Text>
          )}

          <FileManagerImage
            files={
              fmViewProps.view !== "jobs"
                ? isSearching
                  ? searchResponse.files
                  : files
                : undefined
            }
            jobs={
              fmViewProps.view === "jobs"
                ? isSearching
                  ? searchResponse.jobs
                  : jobs
                : undefined
            }
            isLoading={isSearching ? searchResponse.isLoading : isLoading}
            onEndReached={isSearching ? searchResponse.loadMore : loadMore}
            hasMore={isSearching ? searchResponse.hasMore : hasMore}
            loadingText={isSearching ? "Searching..." : "Loading..."}
            settingsCacheKey={PersistedStateKey.FILE_MANAGER_MODEL_EXAMPLES}
            isSelectable
            EmptyStateComponent={AssetsGalleryEmptyState}
            emptyStateProps={emptyStateProps}
            CardActionsComponent={CardActions}
            headerFilterContent={
              <Box w="100%" minW="200px" maxW="300px" pr={2}>
                <Search onSearch={setSearchQuery} placeholder="Search" />
              </Box>
            }
            assetZoomAdditionalFiles={pinnedAssetsFiles}
            scrollRef={contentScrollRef}
            positionerDeps={positionerDeps}
            {...fmViewProps}
            headerFilterAssetProps={fmHeaderFilterAssetProps}
            headerSortProps={isSearching ? undefined : fmHeaderSortProps}
            {...(isOwner
              ? {
                  pinnedFileIds: pinnedAssetIds,
                  onCardPinClick: handleCardPinClick,
                }
              : {})}
          />
        </Flex>
      </Box>
    </VStack>
  );
}

type CardActionsCustomProps = {
  _never?: never;
};

type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({ asset, top, bottom, onActionClick }: CardActionsProps) {
  const assetId = asset?.id;
  const modelId = asset?.metadata.modelId;
  const is3d = asset?.metadata.kind === "3d";
  const isVideo = asset?.metadata.kind === "video";
  const canReuseImage =
    !["vectorization"].includes(asset?.metadata.type ?? "") &&
    !is3d &&
    !isVideo;
  const canReusePrompt = !!asset?.metadata.prompt && !is3d && !isVideo;

  const pathname = is3d
    ? "/3d/new"
    : isVideo
    ? "/videos/new"
    : asset?.metadata.type.includes("texture")
    ? "/textures/new"
    : "/images/new";

  if (!modelId && !canReuseImage && !canReusePrompt) {
    return null;
  }

  return (
    <>
      {top}

      {modelId && (
        <MenuItem
          w="170px"
          iconId="Nav/Model/Outline"
          iconH="12px"
          internalLink={{
            pathname,
            query: {
              modelId,
            },
          }}
          onClick={() => onActionClick?.("useModel")}
          text="Model"
        />
      )}

      {canReuseImage && (
        <MenuItem
          w="170px"
          iconId="Nav/Image/Outline"
          iconH="12px"
          internalLink={{
            pathname,
            query: {
              importAssetId: assetId,
            },
          }}
          onClick={() => onActionClick?.("useImage")}
          text="Image"
        />
      )}

      {canReusePrompt && (
        <MenuItem
          w="170px"
          iconId="Ui/Prompt"
          iconH="12px"
          internalLink={{
            pathname,
            query: {
              importPromptFromAssetId: assetId,
            },
          }}
          onClick={() => onActionClick?.("usePrompt")}
          text="Prompt"
        />
      )}

      {bottom}
    </>
  );
}
